import {Component, Vue} from "vue-property-decorator";
import {Reservation, ReservationAPIInterface, ReservationInterface} from "@/entity/Reservation.ts";
import {RoomInterface} from "@/entity/Room";
import {UserInterface} from "@/entity/User";
import HeaderContainer from "@/components/HeaderContainer.vue";
import BubbleAction from "@/components/BubbleAction.vue";
import moment from "moment";
import Modal from "@/components/Modal.vue";
import ReservationService from "@/services/ReservationService";
import BubbleConfig from "@/types/BubbleConfig";
import Calendar from "@/components/Calendar.vue";
import QuotaLine from "@/components/QuotaLine.vue";
import RecurrenceWrapper from "@/components/RecurrenceWrapper.vue";
import {WrapperConfig, WrapperStatus} from "@/types/WrapperConfig";
import FieldVerifications from "@/utils/FieldVerifications";
import {ExistingDayReservation} from "@/entity/Reservation";
import {QuotaLineConfig, QuotaLineWeek} from "@/types/QuotaLineConfig";
import {ToasterType} from "@/types/ToasterConfig";
import {ErrorService} from "@/services/ErrorService";

export interface CurrentReservationObject {
  [index: string]: any,

  id: number,
  status: number | undefined,
  name: string,
  room: RoomInterface | string | undefined,
  owner: UserInterface | string | undefined,
  recurrenceGroupId: string | null | undefined,
  heureDebut: string,
  heureFin: string,
  day: string,
  wrapperConfig: WrapperConfig;
  indexId: string;
  justification: string;
  isPristine: {
    [index: string]: boolean,
    name: boolean,
    day: boolean,
    heureDebut: boolean,
    heureFin: boolean,
  };
  intervals: {
    [index: string]: number,
    name: number,
    day: number,
    heureDebut: number,
    heureFin: number,
  };
  errors: {
    [index: string]: string,
    name: string,
    day: string,
    heures: string,
  };
  warnings: {
    [index: string]: string,
    name: string,
    day: string,
    heures: string,
  };
  hash: number | undefined;
  pendingReservations: {
    pending: number,
    list: ExistingDayReservation[],
    isIntervalFree: boolean
  },
  quotaLineConfig: QuotaLineConfig,
  previousDate: {
    weekNumber: string,
    year: string,
    day: string,
  },
}

export interface Justification {
  isNeeded: boolean,
  isPristine: boolean,
  error: string,
  warning: string,
  texte: string,
  interval: number
}

enum Mode {
  New = 'new',
  Edit = 'edit',
  View = 'view',
}

enum Recurrence {
  Aucune = '0',
  ToutesLesSemaines = '1',
  TousLesMois = '2',
}

enum ModalAction {
  Delete = 'delete',
  Edit = 'edit',
  Quota = 'quota',
}

enum FormFields {
  Name = 'name',
  Day = 'day',
  StartDate = 'startDate',
  EndDate = 'endDate',
}

@Component({
  components: {
    HeaderContainer,
    BubbleAction,
    Modal,
    Calendar,
    QuotaLine,
    RecurrenceWrapper,
  },
})
export default class ReservationDetail extends Vue {

  public mode: Mode = Mode.View;
  public recurrenceIndex: Recurrence = Recurrence.Aucune;
  public recurrenceName: string[] = ['Aucune', 'Toutes les semaines', 'Tous les mois'];
  public justification: Justification = {
    isNeeded: false,
    isPristine: true,
    error: "",
    warning: "",
    texte: "",
    interval: 0,
  }

  public allOldReservations: ReservationAPIInterface[] = [];
  public allReservations: CurrentReservationObject[] = [];
  public deleteAllRecurrences: boolean = false;

  public isValidationReady: boolean = false;

  //Components
  public bubbleConfig: BubbleConfig = {
    image: require('@/assets/checkmark-white.svg'),
    enabled: false,
  };

  public modalOpened: boolean = false;
  public currentModalAction: ModalAction | false = false;
  public titleModal: string = "";
  public messageModal: string = "";
  public labelModificationModal: string = "";
  public secondaryButtonModal: string = "";
  public primaryButtonModal: string = "";
  public isConfirmationButtonDisabled: boolean = false;
  public isQuotaOverflow: boolean = false;
  public isJustificationNeededForModification: boolean = false;
  public isReservationSending: boolean = false;

  get mainReservation() {
    return this.allReservations[0];
  }

  get getAllRecurrences() {
    return this.allReservations.slice(1);
  }

  async mounted() {
    this.redirectRoute();
    const id = Number(this.$route.params.id);
    if (id === 0) {
      this.mode = Mode.New;
      this.verifyRoute();
      this.allReservations.push(ReservationService.newReservation());
    } else {
      await this.loadMainReservation(id);
      if (this.allOldReservations[0].status === 1 && moment(new Date()) < moment(this.allOldReservations[0].startDate).subtract(2, 'hours')) {
        this.mode = Mode.Edit;
        this.verifyRoute();
      } else {
        this.mode = Mode.View;
        this.verifyRoute();
      }
      const recurrenceId = this.allReservations[0].recurrenceGroupId;
      if (typeof recurrenceId !== "undefined" && recurrenceId !== null) {
        this.loadRecurrencesReservations(recurrenceId);
      } else {

        const initialFormQuota = ReservationService.simulateNewQuota(this.allReservations);
        this.$store.commit('updateInitialFormQuota', initialFormQuota);

        this.updateAllErrors();
        this.justification.isNeeded = ReservationService.isJustificationNeeded(this.allOldReservations);
      }
    }

    if (this.mode === Mode.New || this.mode === Mode.Edit) {
      for (const reservation of this.allReservations) {
        this.$store.dispatch('updateQuotaLine', reservation.quotaLineConfig);
      }
    }
  }

  beforeDestroy() {
    this.$store.commit('unsetQuotaInformations');
  }

  redirectRoute() {
    const [empty, routeName, routeMode, id] = this.$router.currentRoute.path.split('/');
    if ((routeMode === 'new' && id !== '0') || (routeMode !== 'new' && id === '0')) {
      this.$router.push({path: `/${routeName}/new/0`});
    }
  }

  verifyRoute() {
    const [empty, routeName, routeMode, id] = this.$router.currentRoute.path.split('/');
    if (this.mode !== routeMode) {
      this.$router.push({path: `/${routeName}/${this.mode}/${id}`});
    }
  }

  async loadMainReservation(id: number) {
    const mainBackReservation = await ReservationService.getReservation(id);
    if (typeof mainBackReservation === "undefined") this.$router.push({name: 'Home'});
    this.allOldReservations.push(mainBackReservation);
    const mainFrontReservation = ReservationService.formatBackToFront(mainBackReservation);
    mainFrontReservation.isPristine = {
      name: false,
      day: false,
      heureDebut: false,
      heureFin: false,
    };
    mainFrontReservation.hash = ReservationService.hashReservation(mainFrontReservation);
    this.allReservations.push(mainFrontReservation);
  }

  async loadRecurrencesReservations(id: string) {
    ReservationService.getLinkedReservationByRencurrenceGroupId(id, this.mode).then(response => {
      let reservations: ReservationAPIInterface[] = response['hydra:member'];
      const indexMainReservation = reservations.findIndex((reserv) => {
        return reserv.id === this.allReservations[0].id;
      });
      reservations.splice(indexMainReservation, 1);
      for (const index in reservations) {
        const reservation = response['hydra:member'][index];
        this.allOldReservations.push(reservation);

        const formattedReservation = ReservationService.formatBackToFront(reservation);
        formattedReservation.isPristine = {
          name: false,
          day: false,
          heureDebut: false,
          heureFin: false,
        };
        this.allReservations.push(formattedReservation);
      }

      if (this.mode === Mode.New || this.mode === Mode.Edit) {
        for (const reservation of this.allReservations.slice(1)) {
          reservation.hash = ReservationService.hashReservation(reservation);
        }

        const initialFormQuota = ReservationService.simulateNewQuota(this.allReservations);
        this.$store.commit('updateInitialFormQuota', initialFormQuota);

        this.updateAllErrors();
        this.justification.isNeeded = ReservationService.isJustificationNeeded(this.allOldReservations);
      } else {
        for (const reservation of this.allReservations.slice(1)) {
          reservation.wrapperConfig.status = WrapperStatus.None;
          this.updateWrapperName(reservation);
        }
      }
    });
  }

  // Bouton pour supprimer les récurrences
  removeRecurrence(id: string) {
    this.allReservations = this.allReservations.filter(item => item.indexId !== id);
    this.updateValidationButton();
    this.updateQuotaLine();
  }

  // Changement de mode de récurrence
  updateRecurrences() {
    this.allReservations[0].isPristine = {
      name: false,
      day: false,
      heureDebut: false,
      heureFin: false,
    };
    if (this.updateAllErrors()) this.recurrenceIndex = Recurrence.Aucune;

    const mainReservationFromBack = ReservationService.formatFrontToBack(this.allReservations[0]);
    let recurrences: CurrentReservationObject[] = [];
    if (this.recurrenceIndex === Recurrence.ToutesLesSemaines) {
      recurrences = ReservationService.formatArrayBackToFront(this.generateWeeksReservations(mainReservationFromBack));
    } else if (this.recurrenceIndex === Recurrence.TousLesMois) {
      recurrences = ReservationService.formatArrayBackToFront(this.generateMonthsReservations(mainReservationFromBack));
    }

    this.allReservations.splice(1, this.allReservations.length);
    for (const recurrence of recurrences) {
      recurrence.isPristine = {
        name: false,
        day: false,
        heureDebut: false,
        heureFin: false,
      };
      this.allReservations.push(recurrence);
    }
    this.updateAllErrors();
  }

  generateWeeksReservations(mainReservation: ReservationInterface): ReservationInterface[] {
    const recurrencesReservations: ReservationInterface[] = [];
    const startDate = moment(mainReservation.startDate);
    const endDate = moment(mainReservation.endDate);

    for (let i = 1; i < 4; i++) {
      const newRecurrence = new Reservation(mainReservation);
      newRecurrence.startDate = startDate.clone().add(i, 'week').toDate();
      newRecurrence.endDate = endDate.clone().add(i, 'week').toDate();
      recurrencesReservations.push(newRecurrence);
    }
    return recurrencesReservations;
  }

  generateMonthsReservations(mainReservation: ReservationInterface): ReservationInterface[] {
    const recurrencesReservations: ReservationInterface[] = [];
    const startDate = moment(mainReservation.startDate);
    const endDate = moment(mainReservation.endDate);

    for (let i = 1; i < 12; i++) {
      const newRecurrence = new Reservation(mainReservation);
      newRecurrence.startDate = startDate.clone().add(i, 'month').toDate();
      newRecurrence.endDate = endDate.clone().add(i, 'month').toDate();
      recurrencesReservations.push(newRecurrence);
    }
    return recurrencesReservations;
  }

  // Quand on change un champs du formulaire
  fillingField(reservation: CurrentReservationObject, field: FormFields) {
    clearTimeout(reservation.intervals[field]);
    reservation.intervals[field] = setTimeout(() => {
      this.updateReservationErrors(reservation, field);
    }, 750);
  }

  leavingField(reservation: CurrentReservationObject, field: FormFields) {
    clearTimeout(reservation.intervals[field]);
    this.updateReservationErrors(reservation, field);
  }

  // Clique sur bouton bleu de validation-> mode new ou edit
  validationButton() {
    this.justification.isPristine = false;
    if (!this.isValidationReady) return;

    this.isQuotaOverflow = this.isQuotaOverflowing();

    if (this.mode === Mode.New) {
      if (this.isQuotaOverflow) {
        this.openModal(ModalAction.Quota);
      } else {
        this.isReservationSending = true;
        const reservations = ReservationService.formatArrayFrontToBack(this.allReservations);
        this.callPostReservations(reservations);
      }
    } else if (this.mode === Mode.Edit) {
      if (!ReservationService.hasBeenModifiedByHash(this.allReservations)) return;
      this.openModal(ModalAction.Edit);
    }
  }

  isQuotaOverflowing(): boolean {
    const quota: Record<string, QuotaLineWeek> = this.$store.state.quotaLine.quotaData;
    for (const week in quota) {
      if (!quota.hasOwnProperty(week)) continue;
      const initalFormQuota = quota[week].initialFormQuota ? quota[week].initialFormQuota : 0;
      if ((quota[week].usedQuota - initalFormQuota + quota[week].addedQuota) > quota[week].totalQuota) return true;
    }
    return false;
  }

  correctTimes(reservation: CurrentReservationObject) {
    reservation.warnings.heures = "";

    const showWarningStart = this.checkAndCorrectStart(reservation);
    const showWarningEnd = this.checkAndCorrectEnd(reservation);

    if (showWarningStart || showWarningEnd) {
      reservation.warnings.heures = "L'heure ne peut être séléctionnée que par tranche de 15 minutes et à été corrigée automatiquement"
    }
  }

  checkAndCorrectStart(reservation: CurrentReservationObject): boolean {
    const [startHour, startMinute] = reservation.heureDebut.split(":");
    const startMinuteFinal = (((parseInt(startMinute) + 7.5) / 15 | 0) * 15) % 60;

    if (startHour !== "" && startMinute !== "") {
      if (startMinute !== "00" && startMinute !== "15" && startMinute !== "30" && startMinute !== "45") {
        if (startMinuteFinal === 0) {
          if (parseInt(startMinute) > 45) {
            if (startHour !== "23") {
              const firstDigitHour = (parseInt(startHour) + 1).toString().length === 1 ? '0' : '';
              reservation.heureDebut = (firstDigitHour + (parseInt(startHour) + 1).toString() + ":00");
            } else {
              reservation.heureDebut = "23:45";
            }
          } else {
            reservation.heureDebut = (startHour + ":00")
          }
        } else {
          reservation.heureDebut = (startHour + ":" + startMinuteFinal.toString());
        }
        return true;
      }
    }
    return false;
  }

  checkAndCorrectEnd(reservation: CurrentReservationObject): boolean {
    const [endHour, endMinute] = reservation.heureFin.split(":");
    const endMinuteFinal = (((parseInt(endMinute) + 7.5) / 15 | 0) * 15) % 60;

    if (endHour !== "" && endMinute !== "") {
      if (endMinute !== "00" && endMinute !== "15" && endMinute !== "30" && endMinute !== "45") {
        if (endMinuteFinal === 0) {
          if (parseInt(endMinute) > 45) {
            if (endHour !== "23") {
              const firstDigitHour = (parseInt(endHour) + 1).toString().length === 1 ? '0' : '';
              reservation.heureFin = (firstDigitHour + (parseInt(endHour) + 1).toString() + ":00");
            } else {
              reservation.heureFin = "23:45";
            }
          } else {
            reservation.heureFin = (endHour + ":00")
          }
        } else {
          reservation.heureFin = (endHour + ":" + endMinuteFinal.toString());
        }
        return true;
      }
    }
    return false;
  }

  getRecurrenceWithId(indexId: string): CurrentReservationObject {
    return this.allReservations.filter(reservation => {
      return reservation.indexId === indexId
    })[0];
  }

  // Update les erreurs et renvoie true/false si il y a erreur dans le formulaire
  updateAllErrors(): boolean {
    let isError = false;
    for (const reservation of this.allReservations) {
      const isErrorInReservation = this.findReservationErrors(reservation, 'initCheck');
      this.updateWrapper(reservation);

      if (isErrorInReservation) isError = true;
    }

    this.updateValidationButton();
    this.updateQuotaLine();
    return isError;
  }

  updateReservationErrors(reservation: CurrentReservationObject, element: FormFields | false = false): boolean {
    let isError = false;
    if (this.findReservationErrors(reservation, element)) isError = true;

    this.updateWrapper(reservation);
    this.updateValidationButton();
    this.updateQuotaLine();
    return isError;
  }

  findReservationErrors(reservation: CurrentReservationObject, element: FormFields | 'initCheck' | false = false): boolean {
    reservation.errors = {
      name: "",
      day: "",
      heures: "",
    };
    let isErrorInReservation = false;

    this.correctTimes(reservation);
    isErrorInReservation = FieldVerifications.isErrorInName(reservation) ? true : isErrorInReservation;
    const isErrorInHeures = FieldVerifications.isErrorInHeures(reservation);
    isErrorInReservation = isErrorInHeures ? true : isErrorInReservation;
    const isErrorInDay = FieldVerifications.isErrorInDay(reservation);
    isErrorInReservation = isErrorInDay ? true : isErrorInReservation;

    if (!isErrorInDay && element === FormFields.Day || element === 'initCheck') {
      const day = moment(reservation.day, 'YYYY-MM-DD');

      if (reservation.quotaLineConfig.weekNumber !== reservation.previousDate.weekNumber || reservation.quotaLineConfig.year !== reservation.previousDate.year) {
        // update quotaLine
        this.$store.dispatch('updateQuotaLine', reservation.quotaLineConfig);
      }

      if (reservation.quotaLineConfig.weekNumber !== reservation.previousDate.weekNumber || reservation.quotaLineConfig.year !== reservation.previousDate.year || reservation.day !== reservation.previousDate.day) {
        // update availableReservations
        reservation.pendingReservations.pending++;
        ReservationService.getDayReservation(day)
          .then((response: ExistingDayReservation[]) => {
            reservation.pendingReservations.list = response;
            this.updateFreeIntervalErrors(reservation, isErrorInHeures);
          }).catch((error) => {
          ErrorService.dealWithTheError(error);
        }).finally(() => {
          reservation.pendingReservations.pending--;
          this.updateValidationButton();
          this.updateWrapper(reservation);
          this.updateQuotaLine();
        });
      }

    } else if (!isErrorInDay && element === FormFields.StartDate || FormFields.EndDate) {
      this.updateFreeIntervalErrors(reservation, isErrorInHeures);
    }

    return isErrorInReservation;
  }

  updateFreeIntervalErrors(reservation: CurrentReservationObject, isErrorInHeures: boolean): boolean {
    if (isErrorInHeures) {
      reservation.pendingReservations.isIntervalFree = false;
      return true;
    }
    reservation.errors.heures = "";

    const [startHour, startMinute] = reservation.heureDebut.split(":");
    const [endHour, endMinute] = reservation.heureFin.split(":");
    const date = moment(reservation.day, 'YYYY-MM-DD');
    const startTime = date.clone().hour(parseInt(startHour)).minute(parseInt(startMinute));
    const endTime = date.clone().hour(parseInt(endHour)).minute(parseInt(endMinute));

    reservation.pendingReservations.list.filter((reserv: ExistingDayReservation) => {
      if (reserv.id === reservation.id) {
        reservation.pendingReservations.list.splice(reservation.pendingReservations.list.indexOf(reserv), 1);
      }
    });

    const overlapReservations: ExistingDayReservation[] = reservation.pendingReservations.list.filter((reserv: ExistingDayReservation) => {
      if (reserv.id !== reservation.id) {
        const reservStartTimeString = reserv.startDate.toString();
        const reservStartTime = moment(reservStartTimeString.slice(0, 19));
        const reservEndTimeString = reserv.endDate.toString();
        const reservEndTime = moment(reservEndTimeString.slice(0, 19));

        if (startTime.isSameOrBefore(reservStartTime) && endTime.isSameOrAfter(reservEndTime)) return reserv; // Nouvelle réservation est englobée
        if (startTime.isSameOrAfter(reservStartTime) && endTime.isSameOrBefore(reservEndTime)) return reserv; // Nouvelle réservation englobe
        if (startTime.isSameOrAfter(reservStartTime) && startTime.isBefore(reservEndTime)) return reserv; // Chevauche mais dépasse au début
        if (endTime.isAfter(reservStartTime) && endTime.isSameOrBefore(reservEndTime)) return reserv; // Chevauge mais dépasse à la fin
      }
    });

    if (overlapReservations.length > 0) {
      reservation.pendingReservations.isIntervalFree = false;
      reservation.errors.heures = "L'intervalle horaire choisit chevauche une autre réservation";
      return true;
    }

    reservation.pendingReservations.isIntervalFree = true;
    return true;
  }

  updateWrapper(reservation: CurrentReservationObject) {
    this.updateWrapperName(reservation);

    const isPendingEnded = !(reservation.pendingReservations.pending > 0);
    const isIntervalFree = reservation.pendingReservations.isIntervalFree;
    const isFieldsValid = !(reservation.errors.name.length > 0 || reservation.errors.day.length > 0 || reservation.errors.heures.length > 0);
    if (!isPendingEnded) {
      reservation.wrapperConfig.status = WrapperStatus.Loading;
    } else if (isPendingEnded && isIntervalFree && isFieldsValid) {
      reservation.wrapperConfig.status = WrapperStatus.Valid;
    } else {
      reservation.wrapperConfig.status = WrapperStatus.Error;
    }
  }

  updateWrapperName(reservation: CurrentReservationObject) {
    const day = moment(reservation.day, 'YYYY-MM-DD');
    const heureDebut = moment(reservation.heureDebut, 'HH:mm');

    const finalDate = day.set({
      'hour': heureDebut.get('hour'),
      'minute': heureDebut.get('minute')
    });
    let title = finalDate.format('dddd LL à H[h]');
    if (finalDate.format('m') !== "0") title += finalDate.format(' m');
    reservation.wrapperConfig.text = title;
  }

  updateValidationButton() {
    let isValid = true;
    for (const reservation of this.allReservations) {
      if (!ReservationService.isReservationValid(reservation)) isValid = false;
    }
    const isEdited = this.mode === Mode.Edit ? ReservationService.hasBeenModifiedByHash(this.allReservations) : true;
    const isNoPristine = ReservationService.isReservationHasNoPristine(this.allReservations[0]);
    this.isValidationReady = isValid && isNoPristine && isEdited;
    this.bubbleConfig.enabled = this.isValidationReady;
  }

  // mode new
  async callPostReservations(newReservation: ReservationInterface[]) {
    try {
      this.$store.commit('modifyBlockingState', true);
      await ReservationService.postReservations(newReservation);
      this.$store.commit('modifyBlockingState', false);
      this.$store.commit('updateToaster', {
        type: ToasterType.Green,
        message: "La réservation a été créée.",
        isVisible: false,
        visibleOnNextPage: true,
      });
      this.$router.push({name: 'Home'});
    } catch (error) {
      ErrorService.dealWithTheError(error);
    }
  }

  // mode edit
  async editReservations(reservations: ReservationInterface[]) {
    this.isConfirmationButtonDisabled = true;
    try {
      this.$store.commit('modifyBlockingState', true);
      await ReservationService.putReservations(reservations);
      this.$store.commit('modifyBlockingState', false);
      this.$store.commit('updateToaster', {
        type: ToasterType.Green,
        message: "Les modifications ont été prises en compte",
        isVisible: false,
        visibleOnNextPage: true,
      });
      this.$router.push({name: 'Home'});
    } catch (error) {
      ErrorService.dealWithTheError(error);
    }

    this.isConfirmationButtonDisabled = false;
  }

  // mode edit | appelé par la modale après un clique sur la poubelle
  async deleteReservations(reservations: ReservationInterface[]) {
    this.isConfirmationButtonDisabled = true;
    try {
      this.$store.commit('modifyBlockingState', true);
      const response = await ReservationService.deleteReservations(reservations);
      this.$store.commit('modifyBlockingState', false);
      this.$store.commit('updateToaster', {
        type: ToasterType.Green,
        message: (reservations.length > 1) ? "La suppression de la réservation et de ses récurrences a été prise en compte" : "La suppression de la réservation a été prise en compte",
        isVisible: false,
        visibleOnNextPage: true,
      });
      this.$router.push({name: 'Home'});
    } catch (error) {
      ErrorService.dealWithTheError(error);
    }

    this.isConfirmationButtonDisabled = false;
  }

  updateJustificationError() {
    clearTimeout(this.justification.interval);
    this.justification.interval = setTimeout(() => {
      FieldVerifications.isErrorInJustification(this.justification);
    }, 750);
  }

  // return true if success
  fillJustificationInReservations(reservations: ReservationInterface[]) {

    const limitJustificationNeeded = moment().startOf('day').add(2, 'days');
    for (const reservation of reservations) {
      const oldReservation = this.allOldReservations.filter((reserv: ReservationAPIInterface) => {
        return reserv.id === reservation.id;
      })[0];
      const startDate = moment(oldReservation.startDate);
      if (limitJustificationNeeded.isAfter(startDate)) reservation.log = this.justification.texte;
    }
  }

  //
  // LES COMPOSANTS

  historyBack() {
    const history = this.$store.state.historyModule.routesPath;
    const lastPage = history.filter((path: string) => {
      if (path === '/reservations' || path === '/') return true;
    });
    if (lastPage.length === 0) {
      this.$router.push({name: 'Login'});
    } else {
      this.$router.push({path: lastPage[0]});
    }
  }

  // L'input date (Calendar)
  onDateClose(indexId: string, newDate: Date | null = null) {
    const reservation = this.getRecurrenceWithId(indexId);
    reservation.isPristine.day = false;
    if (newDate !== null) {
      const day = moment(newDate);

      reservation.previousDate.day = reservation.day ? reservation.day : '';
      reservation.previousDate.weekNumber = reservation.quotaLineConfig.weekNumber ? reservation.quotaLineConfig.weekNumber : '';
      reservation.previousDate.year = reservation.quotaLineConfig.year ? reservation.quotaLineConfig.year : '';

      reservation.day = day.format('YYYY-MM-DD');
      reservation.quotaLineConfig.weekNumber = day.format('WW');
      reservation.quotaLineConfig.year = day.format('YYYY');
    }
    this.updateReservationErrors(reservation, FormFields.Day);
  }

  // La modale
  openModal(action: ModalAction) {
    if (this.currentModalAction !== action) {
      if (action === ModalAction.Delete) {
        this.titleModal = "Suppression";
        this.messageModal = "Voulez-vous supprimer votre réservation ?";
        this.labelModificationModal = "Motif de la suppression";
        this.secondaryButtonModal = "Annuler";
        this.primaryButtonModal = "Supprimer";
      } else if (action === ModalAction.Edit) {
        this.titleModal = "Modification";
        this.messageModal = "Êtes-vous sûr de vouloir modifier la réservation ?";
        this.labelModificationModal = "Motif de la modification";
        this.secondaryButtonModal = "Annuler";
        this.primaryButtonModal = "Modifier";
      } else if (action === ModalAction.Quota) {
        this.titleModal = "Dépassement de quota";
        this.messageModal = "Vous avez utilisé l'ensemble de votre quota pour au moins une réservation. Le quota supplémentaire utilisé sera facturé. Êtes-vous sûr de vouloir valider la réservation ?";
        this.labelModificationModal = "";
        this.secondaryButtonModal = "Annuler";
        this.primaryButtonModal = "Valider";
      }
      this.currentModalAction = action;
    }
    this.updateJustificationVisibility();
    this.modalOpened = true;
  }

  closeModal() {
    this.modalOpened = false;
  }

  validateModal() {
    this.justification.isPristine = false;

    if (this.mode === Mode.New) {
      if (this.currentModalAction === ModalAction.Quota) {
        this.isReservationSending = true;
        const reservations = ReservationService.formatArrayFrontToBack(this.allReservations);
        this.callPostReservations(reservations);
      }
    } else if (this.mode === Mode.Edit) {
      if (this.currentModalAction === ModalAction.Delete) {
        if (this.deleteAllRecurrences) {
          const deletedReservations = ReservationService.formatArrayFrontToBack(this.allReservations);

          this.isJustificationNeededForModification = ReservationService.isJustificationNeeded(this.allOldReservations);
          if (this.isJustificationNeededForModification) {
            if (FieldVerifications.isErrorInJustification(this.justification)) return;
            this.isReservationSending = true;
            this.fillJustificationInReservations(deletedReservations);
          }
          this.isReservationSending = true;
          this.deleteReservations(deletedReservations);
        } else {
          const deletedReservations = [ReservationService.formatFrontToBack(this.allReservations[0])];

          this.isJustificationNeededForModification = ReservationService.isJustificationNeededForReservation(this.allOldReservations[0]);
          if (this.isJustificationNeededForModification) {
            if (FieldVerifications.isErrorInJustification(this.justification)) return;
            this.isReservationSending = true;
            this.fillJustificationInReservations(deletedReservations);
          }
          this.isReservationSending = true;
          this.deleteReservations(deletedReservations);
        }
      } else if (this.currentModalAction === ModalAction.Edit) {
        const editedReservations = ReservationService.formatArrayFrontToBack(ReservationService.getEditedReservationsByHash(this.allReservations));
        const editedReservationsApi: ReservationAPIInterface[] = ReservationService.getReservationsApiFromReservations(editedReservations, this.allOldReservations);

        this.isJustificationNeededForModification = ReservationService.isJustificationNeeded(editedReservationsApi);
        if (this.isJustificationNeededForModification) {
          if (FieldVerifications.isErrorInJustification(this.justification)) return;
          this.isReservationSending = true;
          this.fillJustificationInReservations(editedReservations);
        }
        this.isReservationSending = true;
        this.editReservations(editedReservations);
      }
    }
    this.closeModal();
  }

  updateJustificationVisibility() {
    if (this.mode === Mode.Edit) {
      if (this.currentModalAction === ModalAction.Delete) {
        if (this.deleteAllRecurrences) {
          this.isJustificationNeededForModification = ReservationService.isJustificationNeeded(this.allOldReservations);
        } else {
          this.isJustificationNeededForModification = ReservationService.isJustificationNeededForReservation(this.allOldReservations[0]);
        }
      } else if (this.currentModalAction === ModalAction.Edit) {
        const editedReservations = ReservationService.formatArrayFrontToBack(ReservationService.getEditedReservationsByHash(this.allReservations));
        const editedReservationsApi: ReservationAPIInterface[] = ReservationService.getReservationsApiFromReservations(editedReservations, this.allOldReservations);
        this.isJustificationNeededForModification = ReservationService.isJustificationNeeded(editedReservationsApi);
      }
    }
  }

  // QuotaLine
  updateQuotaLine() {
    const newQuota = ReservationService.simulateNewQuota(this.allReservations);
    if (Object.keys(newQuota).length === 0) return;
    this.$store.commit('updateAddedQuota', newQuota);
  }


}
